/* eslint-disable sort-keys */
import strutBackend from 'apis/strutBackend';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { isAuthenticationError, ServerError } from './handleError';

type Headers = { [key: string]: string };
export interface IRestFactory {
  get: <T>(
    url: string,
    params?: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ) => Promise<T>;
  post: <T>(
    url: string,
    data?: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ) => Promise<T>;
  put: <T>(
    url: string,
    data: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ) => Promise<T>;
  patch: <T>(
    url: string,
    data?: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ) => Promise<T>;
  delete: <T>(
    url: string,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ) => Promise<T>;
}

const restService = (): IRestFactory => ({
  async get<T>(
    url: string,
    params?: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ): Promise<T> {
    return await makeRequest('get', url, params, headers, additionalConfig);
  },
  async post<T>(
    url: string,
    data?: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ): Promise<T> {
    return await makeRequest('post', url, data, headers, additionalConfig);
  },

  async put<T>(
    url: string,
    data: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ): Promise<T> {
    return await makeRequest('put', url, data, headers, additionalConfig);
  },

  async patch<T>(
    url: string,
    data: unknown,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ): Promise<T> {
    return await makeRequest('patch', url, data, headers, additionalConfig);
  },

  async delete<T>(
    url: string,
    headers?: Headers,
    additionalConfig?: AxiosRequestConfig
  ): Promise<T> {
    return await makeRequest(
      'delete',
      url,
      undefined,
      headers,
      additionalConfig
    );
  },
});

type Method = 'post' | 'put' | 'patch' | 'delete' | 'get';

async function makeRequest(
  method: Method,
  url: string,
  data?: unknown,
  headers: Headers = {},
  additionalConfig?: AxiosRequestConfig
) {
  try {
    const axiosConfig: AxiosRequestConfig = {
      method,
      [method === 'get' ? 'params' : 'data']: data,
      url,
      headers,
      ...additionalConfig,
    };
    await setAuthHeader(axiosConfig);
    const { data: response } = (await strutBackend(
      axiosConfig
    )) as AxiosResponse;
    return response;
  } catch (error) {
    throw buildApiError(error as AxiosError<ApiError>);
  }
}

async function setAuthHeader(axiosConfig: AxiosRequestConfig) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const token = user?.jwt;
  if (token || axiosConfig.headers?.Authorization) {
    return (axiosConfig.headers = {
      Authorization: `Bearer ${token}`,
      ...axiosConfig.headers,
    });
  }
  return axiosConfig;
}

export interface ApiError {
  message: Array<{
    messages: Array<{
      message: string;
    }>;
  }>;
  detail: string;
}

export function buildApiError(error: AxiosError<ApiError>) {
  if (isAuthenticationError(error as unknown as AxiosError<ServerError>)) {
    return error;
  }

  // When is not an authentication error we return the error message, not the error object.
  // This is because we want to show the error string in a toast.
  // Authentication error will redirect the user to the /logout page.
  return (
    error.response?.data?.message?.[0].messages?.[0].message ||
    error.response?.data?.message ||
    (error.response?.data && Object.values(error.response?.data).join('\n')) ||
    (!error.isAxiosError && error.message) ||
    error
  );
}

export default restService();
